import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { Image } from 'foundations-components/transitional/components/Image';
import { Logo } from 'foundations-components/transitional/components/Logo';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { generatePath } from 'react-router-dom';
import SanitizedText from 'sanitize-text/components/SanitizedText';
import styled, { keyframes } from 'styled-components';
import RouterButton from 'ui-addon-react-router-dom/RouterButton';
import { Paths } from '../../constants/paths';
import { useScrollToTop } from '../../hooks/useScrollToTop';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function UmbrellaCollectionCard({
  title,
  description,
  slug,
  onClick
}) {
  const scrollToTop = useScrollToTop();
  const {
    isMobile
  } = useResponsiveProps({
    isMobile: {
      xs: true,
      md: false
    }
  });
  return /*#__PURE__*/_jsx(CardWrapper, {
    margin: "0",
    blockSize: "100%",
    overflow: "hidden",
    children: /*#__PURE__*/_jsx(CardSection, {
      blockSize: "100%",
      padding: "0",
      margin: "0",
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "stretch",
        flexDirection: isMobile ? 'column-reverse' : 'row',
        gap: "0",
        justifyContent: "space-between",
        minBlockSize: "auto",
        blockSize: "100%",
        margin: "0",
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "start",
          flexDirection: "column",
          flex: isMobile ? '1' : '0 0 50%',
          justifyContent: "center",
          padding: isMobile ? '600' : '1200',
          children: [/*#__PURE__*/_jsx(Text, {
            as: "h5",
            marginBlockEnd: isMobile ? '400' : '800',
            typography: isMobile ? 'heading-300' : 'heading-600',
            children: title
          }), /*#__PURE__*/_jsx(Text, {
            marginBlockEnd: isMobile ? '400' : '1100',
            children: /*#__PURE__*/_jsx(SanitizedText, {
              body: description,
              WrapperComponent: "span"
            })
          }), /*#__PURE__*/_jsx(RouterButton, {
            external: false,
            marginBlockStart: "auto",
            onClick: () => {
              onClick === null || onClick === void 0 || onClick();
              scrollToTop();
            },
            to: generatePath(Paths.FEATURED_COLLECTION, {
              collectionSlug: slug
            }),
            use: "tertiary-extra-light",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplaceStorefront.collectionCard.browseNow"
            })
          })]
        }), /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          flex: isMobile ? '1' : '0 0 50%',
          justifyContent: "center",
          minBlockSize: "200px",
          padding: "600",
          margin: "0",
          position: "relative",
          overflow: "hidden",
          children: /*#__PURE__*/_jsx(Fancy, {})
        })]
      })
    })
  });
}
const LOGOS = ['https://hubspot-integrators-prod.s3.amazonaws.com/listing/6922949.png',
// OpenAI
'https://521324.fs1.hubspotusercontent-na1.net/hubfs/521324/__hs-marketplace__/anthropic-icon-logo-png_seeklogo-515014-1.png',
// Anthropic
'https://521324.fs1.hubspotusercontent-na1.net/hubfs/521324/__hs-marketplace__/snowflake-bug-color-rgb@2x-1.png',
// Snowflake
'https://521324.fs1.hubspotusercontent-na1.net/hubfs/521324/__hs-marketplace__/Asana-Dots-Coral-1-1.png',
// Asana
'https://hubspot-integrators-prod.s3.amazonaws.com/listing/542084.png',
// Shopify
'https://5450265.fs1.hubspotusercontent-na1.net/hubfs/5450265/__hs-marketplace__/SendosoS_RGB_Orange.jpeg',
// Sendoso
'https://521324.fs1.hubspotusercontent-na1.net/hubfs/521324/__hs-marketplace__/Zoom%20logo-2-1.png',
// Zoom
'https://20277870.fs1.hubspotusercontent-na1.net/hubfs/20277870/__hs-marketplace__/AMPLITUDE_%5BMARK-CIRCLE%5D_BLUE-1.png',
// Amplitude
'https://f.hubspotusercontent00.net/hubfs/8157854/__hs-marketplace__/pendo-chevron-pink-1.png',
// Pendo
'https://cdn2.hubspot.net/hubfs/521324/New%20Jira%20Logo-1.png',
// Jira
'https://521324.fs1.hubspotusercontent-na1.net/hubfs/521324/__hs-marketplace__/Group%207-1.png',
// Google Drive
'https://f.hubspotusercontent20.net/hubfs/521324/__hs-marketplace__/Logo%20%7C%20Microsoft%20Teams-1.png',
// Microsoft Teams
'https://hubspot-integrators-prod.s3.amazonaws.com/listing/235997.png',
// Xero
'https://521324.fs1.hubspotusercontent-na1.net/hubfs/521324/__hs-marketplace__/image%2012-1.png',
// Box
'https://521324.fs1.hubspotusercontent-na1.net/hubfs/521324/__hs-marketplace__/Group%203-1.png',
// Outlook
'https://521324.fs1.hubspotusercontent-na1.net/hubfs/521324/__hs-marketplace__/Group%202-1.png',
// Gmail
'https://521324.fs1.hubspotusercontent-na1.net/hubfs/521324/__hs-marketplace__/Group%206-1.png',
// OneDrive
'https://521324.fs1.hubspotusercontent-na1.net/hubfs/521324/__hs-marketplace__/Group%205-1.png' // Confluence
];
const shuffledLogos = LOGOS.sort(() => Math.random() - 0.5);
const LOGO_SIZE = '70px';
const INNER_PERIOD = '180s';
const OUTER_PERIOD = '200s';
const INNER_RING_COUNT = 6;
const OUTER_RING_COUNT = 12;
const INNER_RADIUS = '-120px';
const OUTER_RADIUS = '-240px';
function ListingLogo({
  src
}) {
  return /*#__PURE__*/_jsx(Image, {
    loading: "lazy",
    inlineSize: LOGO_SIZE,
    blockSize: LOGO_SIZE,
    src: src
  });
}
const rotateClockwise = keyframes(["from{transform:rotate(0deg);}to{transform:rotate(360deg);}"]);
const rotateCounterClockwise = keyframes(["from{transform:rotate(0deg);}to{transform:rotate(-360deg);}"]);
const RingContainer = styled.div.withConfig({
  displayName: "UmbrellaCollectionCard__RingContainer",
  componentId: "sc-18dszgx-0"
})(["position:absolute;display:flex;align-items:center;justify-content:center;min-width:428px;"]);
const RotatingRing = styled.div.withConfig({
  displayName: "UmbrellaCollectionCard__RotatingRing",
  componentId: "sc-18dszgx-1"
})(["position:absolute;width:1000px;height:1000px;animation:", " ", " linear infinite;"], props => props.keyframes, props => props.period);
const LogoWrapper = styled.div.withConfig({
  displayName: "UmbrellaCollectionCard__LogoWrapper",
  componentId: "sc-18dszgx-2"
})(["position:absolute;left:50%;top:50%;transform:translate(-50%,-50%) rotate(", "deg) translateY(", ") rotate(", "deg);"], props => props.angle, props => props.offset, props => -props.angle);
const LogoContent = styled.div.withConfig({
  displayName: "UmbrellaCollectionCard__LogoContent",
  componentId: "sc-18dszgx-3"
})(["animation:", " ", " linear infinite;"], props => props.keyframes, props => props.period);
const CenterLogo = styled.div.withConfig({
  displayName: "UmbrellaCollectionCard__CenterLogo",
  componentId: "sc-18dszgx-4"
})(["position:absolute;z-index:1;"]);
function Fancy() {
  const innerRingLogos = shuffledLogos.slice(0, INNER_RING_COUNT);
  const outerRingLogos = shuffledLogos.slice(INNER_RING_COUNT, INNER_RING_COUNT + OUTER_RING_COUNT);
  return /*#__PURE__*/_jsxs(RingContainer, {
    children: [/*#__PURE__*/_jsx(RotatingRing, {
      keyframes: rotateClockwise,
      period: INNER_PERIOD,
      children: innerRingLogos.map((logo, index) => /*#__PURE__*/_jsx(LogoWrapper, {
        angle: index * 360 / INNER_RING_COUNT,
        offset: INNER_RADIUS,
        children: /*#__PURE__*/_jsx(LogoContent, {
          keyframes: rotateCounterClockwise,
          period: INNER_PERIOD,
          children: /*#__PURE__*/_jsx(ListingLogo, {
            src: logo
          })
        })
      }, index))
    }), /*#__PURE__*/_jsx(RotatingRing, {
      keyframes: rotateCounterClockwise,
      period: OUTER_PERIOD,
      children: outerRingLogos.map((logo, index) => /*#__PURE__*/_jsx(LogoWrapper, {
        angle: index * 360 / OUTER_RING_COUNT,
        offset: OUTER_RADIUS,
        children: /*#__PURE__*/_jsx(LogoContent, {
          keyframes: rotateClockwise,
          period: OUTER_PERIOD,
          children: /*#__PURE__*/_jsx(ListingLogo, {
            src: logo
          })
        })
      }, index))
    }), /*#__PURE__*/_jsx(CenterLogo, {
      children: /*#__PURE__*/_jsx(Logo, {
        name: "breeze-symbol-gradient",
        width: LOGO_SIZE,
        height: LOGO_SIZE
      })
    })]
  });
}